'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { getStoredUserRaw } from '@/lib/authStorage';
import MysteryRoomCreateLobby from './MysteryRoomCreateLobby';
import MysteryRoomMapCard from './MysteryRoomMapCard';
import type { MrFriend, MrMapSummary, MrProfileResponse, MrLobbyResponse, MrStatsPlayer } from './runtime/net/mysteryRoomApi';
import { MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN } from './runtime/world/characterAssets';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';
import { formatMysteryRoomFriendPresence, isMysteryRoomFriendPlaying, sortMysteryRoomFriends } from './friendPresence';
import {
  MYSTERY_ROOM_DEFAULT_WANDER_BOTS,
  MYSTERY_ROOM_MAX_WANDER_BOTS,
  clampWanderBotCount,
} from './mysteryRoomWanderDefaults';

function WanderBotsStepper({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label?: string;
}) {
  const v = clampWanderBotCount(value);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-[12px] border border-site-border bg-black/30 px-3 py-2.5">
      <span className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-site-muted">{label ?? 'Боты на карте'}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="grid size-8 place-items-center rounded-lg border border-site-border text-lg font-black text-site-text hover:bg-site-text/10 disabled:opacity-35"
          aria-label="Меньше ботов"
          disabled={v <= 0}
          onClick={() => onChange(v - 1)}
        >
          −
        </button>
        <span className="min-w-[2.25rem] text-center font-black tabular-nums text-site-text">{v}</span>
        <button
          type="button"
          className="grid size-8 place-items-center rounded-lg border border-site-border text-lg font-black text-site-text hover:bg-site-text/10 disabled:opacity-35"
          aria-label="Больше ботов"
          disabled={v >= MYSTERY_ROOM_MAX_WANDER_BOTS}
          onClick={() => onChange(v + 1)}
        >
          +
        </button>
      </div>
      <span className="w-full text-[0.62rem] text-site-muted">Одиночка и хост P2P · 0–{MYSTERY_ROOM_MAX_WANDER_BOTS}</span>
    </div>
  );
}

type MenuView = 'home' | 'create' | 'about';
type MenuTab = 'play' | 'heroes' | 'stats' | 'settings';
type MenuSettingKey = 'motion' | 'compactFriends' | 'menuAudio' | 'autoInventory';
type TopTabKey = 'profile' | 'missions' | 'gear' | 'database' | 'contracts' | 'shop';

const TOP_TABS: { key: TopTabKey; label: string }[] = [
  { key: 'profile', label: 'ПРОФИЛЬ' },
  { key: 'missions', label: 'МИССИИ' },
  { key: 'gear', label: 'СНАРЯЖЕНИЕ' },
  { key: 'database', label: 'БАЗА ДАННЫХ' },
  { key: 'contracts', label: 'КОНТРАКТЫ' },
  { key: 'shop', label: 'МАГАЗИН' },
];

type MrLobbySnapshot = NonNullable<MrLobbyResponse['lobby']>;

type MainMenuP2POverrides = {
  mapId?: string;
  bots?: number;
  botsOnly?: boolean;
  gameMode?: 'coop' | 'survival' | 'stealth' | 'infection';
  botDifficulty?: 'low' | 'mid' | 'high';
  globalMission?: 'evac' | 'last_voice' | 'cleanup';
  zombieWaveCount?: number;
  zombieWaveSize?: number;
};

type Props = {
  view: MenuView;
  selectedMapTitle: string;
  selectedMapId: string;
  selectedMapKind: 'builtin' | 'custom';
  selectedMapDefinition: MysteryRoomMapDefinition;
  mapStatus: string;
  mapsAuthRequired: boolean;
  mapsCatalogLoaded: boolean;
  publishedMapsCount: number;
  publishedMaps: MrMapSummary[];
  myMaps: MrMapSummary[];
  myMapsStatus: string;
  profile: MrProfileResponse['profile'] | null;
  profileStatus: string;
  leaderboard: MrStatsPlayer[];
  friends: MrFriend[];
  friendsStatus: string;
  inviteStatusByFriendId: Record<number, string>;
  onFindGame: () => void;
  onCreateGame: () => void;
  onStartLobby: (options: {
    botCount?: number;
    botsOnly?: boolean;
    gameMode?: 'coop' | 'survival' | 'stealth' | 'infection';
    botDifficulty?: 'low' | 'mid' | 'high';
    globalMission?: 'evac' | 'last_voice' | 'cleanup';
    zombieWaveCount?: number;
    zombieWaveSize?: number;
    mrLinkedLobbyCode?: string | null;
    syncedMrLobby?: MrLobbySnapshot;
  }) => void;
  /** Подключение к P2P по коду лобби или ссылке с matchId */
  onJoinP2PLobby: (rawCodeOrUrl: string, menuOverrides?: MainMenuP2POverrides | null) => void;
  onOpenAbout: () => void;
  onBackHome: () => void;
  /** P2P смена на паузе (URL без mode=play, есть matchId): вернуться в игру */
  canResumePausedP2p?: boolean;
  onResumeP2pSession?: () => void;
  onOpenCatalog: () => void;
  onOpenEditor: () => void;
  onOpenLocalPreview: () => void;
  onQuickStart: () => void;
  onSelectBuiltin: () => void;
  onSelectCustomMap: (mapId: string) => void;
  onInviteFriend: (friend: MrFriend) => void;
  onExitGame: () => void;
  /** Число блуждающих ботов по умолчанию (одиночка, P2P без решётки лобби) */
  wanderBotsPreference?: number;
  onWanderBotsPreferenceChange?: (n: number) => void;
  /** Вкладка экрана лобби (создание / поиск) из URL */
  initialLobbyNavTab?: 'create' | 'find';
  currentLobbyMode?: 'any' | 'owned';
  onLobbyNavTabChange?: (tab: 'create' | 'find') => void;
};

const BUILTIN_MAP_ID = 'lisad_9_night_shift';

/** Текст брифа: встроенная карта или описание из каталога авторских карт */
function resolveMissionBriefBody(
  selectedMapId: string,
  selectedMapKind: 'builtin' | 'custom',
  publishedMaps: MrMapSummary[],
  myMaps: MrMapSummary[],
): string {
  const builtin = selectedMapKind === 'builtin' || selectedMapId === BUILTIN_MAP_ID;
  if (builtin) {
    return 'Базовая лаборатория для быстрого старта, лобби и проверки механик. Ночная смена на LISAD-9: зоны доступа, патрули NPC, скрытые задачи и реакция объекта на тревогу.';
  }
  const row = [...publishedMaps, ...myMaps].find((m) => m.id === selectedMapId || m.mapKey === selectedMapId);
  return row?.description?.trim() || 'Карта сообщества — начните в одиночку или соберите смену по сети.';
}

type MissionBriefOverlayProps = Pick<
  Props,
  | 'selectedMapTitle'
  | 'selectedMapId'
  | 'selectedMapKind'
  | 'mapStatus'
  | 'publishedMaps'
  | 'myMaps'
  | 'onQuickStart'
  | 'onFindGame'
  | 'onCreateGame'
  | 'onOpenCatalog'
  | 'wanderBotsPreference'
  | 'onWanderBotsPreferenceChange'
> & { onClose: () => void };

function MissionBriefOverlay(props: MissionBriefOverlayProps) {
  const [path, setPath] = useState<null | 'solo' | 'net'>(null);
  const body = resolveMissionBriefBody(
    props.selectedMapId,
    props.selectedMapKind,
    props.publishedMaps,
    props.myMaps,
  );
  const soloActive = path === 'solo';
  const netActive = path === 'net';
  const soloCardCls = `flex min-h-[104px] flex-col items-start gap-1 rounded-[14px] border p-4 text-left transition outline-none ${
    soloActive
      ? 'border-site-warning/55 bg-gradient-to-br from-site-warning/14 to-transparent shadow-[inset_0_0_0_1px_rgba(247,215,144,0.15)]'
      : 'border-site-border bg-black/28 hover:border-site-warning/35'
  }`;
  const netCardCls = `flex min-h-[104px] flex-col items-start gap-1 rounded-[14px] border p-4 text-left transition outline-none ${
    netActive
      ? 'border-site-accent/50 bg-gradient-to-br from-site-accent/12 to-transparent shadow-[inset_0_0_0_1px_rgba(103,232,249,0.12)]'
      : 'border-site-border bg-black/28 hover:border-site-accent/30'
  }`;

  return (
    <div className="mr-ref-overlay mr-ref-overlay--lobby" role="dialog" aria-modal="true" aria-label="Подробности миссии">
      <div className="mr-ref-overlay-head">
        <span className="mr-ref-overlay-title max-w-[min(100%,28rem)] truncate" title={props.selectedMapTitle}>
          МИССИЯ · {props.selectedMapTitle}
        </span>
        <button type="button" onClick={props.onClose} className="mr-ref-overlay-close" aria-label="Закрыть">
          ×
        </button>
      </div>
      <div className="mr-ref-overlay-body">
        <div className="mx-auto flex max-w-lg flex-col gap-7">
          <div>
            <div className="mr-ref-section-label">ОПИСАНИЕ</div>
            <p className="mt-2 text-[0.9rem] leading-relaxed text-site-secondary">{body}</p>
            <div className="mt-3 inline-flex rounded-lg border border-site-border bg-site-surface/[0.04] px-3 py-1 text-[0.72rem] text-site-muted">
              Статус · <span className="text-site-text">{props.mapStatus}</span>
            </div>
          </div>

          <div>
            <div className="mr-ref-section-label">НАГРАДЫ</div>
            <div className="mt-3 flex flex-wrap items-center gap-8">
              <span className="mr-ref-reward mr-ref-reward-xp">XP&nbsp;&nbsp;8500</span>
              <span className="mr-ref-reward mr-ref-reward-coin">₽&nbsp;&nbsp;7 500</span>
            </div>
          </div>

          <div className="border-t border-site-border pt-6">
            <div className="mr-ref-section-label mb-1">СТАРТ МИССИИ</div>
            <p className="mb-4 text-[0.78rem] text-site-muted">Выберите ветку — откроется следующий шаг</p>

            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" className={soloCardCls} onClick={() => setPath('solo')}>
                <span className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-site-warning/85">одиночка</span>
                <span className="text-[1rem] font-bold text-site-text">Одиночная смена</span>
                <span className="text-[0.75rem] leading-snug text-site-muted">Локальная игра без ожидания матча</span>
              </button>
              <button type="button" className={netCardCls} onClick={() => setPath('net')}>
                <span className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-site-accent/85">сеть</span>
                <span className="text-[1rem] font-bold text-site-text">По сети</span>
                <span className="text-[0.75rem] leading-snug text-site-muted">Подбор игроков или своё лобби (P2P)</span>
              </button>
            </div>

            {path ? (
              <div className="flex justify-center py-2 text-site-muted" aria-hidden>
                ↓
              </div>
            ) : null}

            {soloActive ? (
              <div className="overflow-hidden rounded-[14px] border border-site-warning/25 bg-gradient-to-r from-site-warning/10 to-black/40 p-4">
                <div className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.12em] text-site-warning/90">Одиночка · запуск</div>
                <WanderBotsStepper
                  value={props.wanderBotsPreference ?? MYSTERY_ROOM_DEFAULT_WANDER_BOTS}
                  onChange={(n) => props.onWanderBotsPreferenceChange?.(clampWanderBotCount(n))}
                  label="Боты на карте"
                />
                <button
                  type="button"
                  className="mr-ref-play-button mt-4 w-full max-w-none"
                  onClick={() => {
                    props.onQuickStart();
                    props.onClose();
                  }}
                >
                  <span>Начать миссию</span>
                  <small>локальная сессия</small>
                </button>
              </div>
            ) : null}

            {netActive ? (
              <div className="rounded-[14px] border border-site-accent/22 bg-gradient-to-br from-site-bg/90 to-black/55 p-4">
                <div className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.12em] text-site-accent/85">Сеть · выбор режима</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    className="flex flex-col gap-1 rounded-xl border border-site-success/35 bg-site-success/[0.08] px-4 py-3 text-left transition hover:bg-site-success/[0.14]"
                    onClick={() => {
                      props.onFindGame();
                      props.onClose();
                    }}
                  >
                    <span className="text-[0.9rem] font-bold text-site-success">Найти матч</span>
                    <span className="text-[0.72rem] text-site-success/75">Быстрый вход в онлайн-смену</span>
                  </button>
                  <button
                    type="button"
                    className="flex flex-col gap-1 rounded-xl border border-site-accent/35 bg-site-accent/[0.07] px-4 py-3 text-left transition hover:bg-site-accent/[0.12]"
                    onClick={() => {
                      props.onCreateGame();
                      props.onClose();
                    }}
                  >
                    <span className="text-[0.9rem] font-bold text-site-text">Создать лобби</span>
                    <span className="text-[0.72rem] text-site-muted">Состав комнаты и приглашение</span>
                  </button>
                </div>
              </div>
            ) : null}

            <div className="mt-8 flex justify-center border-t border-dashed border-site-border pt-5">
              <button
                type="button"
                className="text-[0.72rem] font-semibold uppercase tracking-[0.1em] text-site-muted underline-offset-4 hover:text-site-warning/90 hover:underline"
                onClick={() => {
                  props.onOpenCatalog();
                  props.onClose();
                }}
              >
                Другая миссия — каталог
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const HEROES = [
  {
    id: 'anna',
    name: 'Анна',
    role: 'Оперативник',
    status: 'Выбрана',
    price: 0,
    imageUrl: MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.menuUrl,
  },
  { id: 'medic', name: 'Медик', role: 'Поддержка', status: 'Скоро', price: 420, imageUrl: null },
  { id: 'engineer', name: 'Инженер', role: 'Системы', status: 'Скоро', price: 560, imageUrl: null },
  { id: 'shadow', name: 'Тень', role: 'Скрытность', status: 'Скоро', price: 760, imageUrl: null },
] as const;

const STORE_ITEMS = [
  { id: 'visor', title: 'Неоновый визор', detail: 'Косметика героя', price: 180, tone: 'cyan' },
  { id: 'coat', title: 'Куртка охраны', detail: 'Скин смены', price: 320, tone: 'amber' },
  { id: 'emote', title: 'Жест тревоги', detail: 'Эмоция в матче', price: 140, tone: 'emerald' },
] as const;

/** Фон и герой раздельно — лёгкий параллакс в `.mr-ref-shell--parallax` */
const MENU_LAB_BG_URL = '/games/mystery-room/menu/menu-lab-bg.png';
const MENU_HERO_FULL_URL = '/games/mystery-room/menu/menu-hero-full.png';

type ReferenceFriendDisplay = {
  name: string;
  status: string;
  tone: 'online' | 'mission' | 'lobby' | 'away';
  avatarUrl?: string | null;
  /** Ссылка с бэкенда (лобби/матч), не P2P */
  joinUrl?: string | null;
};

type StoredMenuUser = {
  username?: string | null;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
  avatar_display_url?: string | null;
};

export default function MysteryRoomMainMenu(props: Props) {
  if (props.view === 'about') {
    return <AboutScreen {...props} />;
  }

  if (props.view === 'create') {
    return (
      <MysteryRoomCreateLobby
        selectedMapTitle={props.selectedMapTitle}
        selectedMapId={props.selectedMapId}
        selectedMapKind={props.selectedMapKind}
        selectedMapDefinition={props.selectedMapDefinition}
        mapStatus={props.mapStatus}
        mapsAuthRequired={props.mapsAuthRequired}
        publishedMaps={props.publishedMaps}
        myMaps={props.myMaps}
        profile={props.profile}
        friends={props.friends}
        friendsStatus={props.friendsStatus}
        inviteStatusByFriendId={props.inviteStatusByFriendId}
        onSelectBuiltin={props.onSelectBuiltin}
        onSelectCustomMap={props.onSelectCustomMap}
        onStartLobby={props.onStartLobby}
        onJoinP2PLobby={props.onJoinP2PLobby}
        onInviteFriend={props.onInviteFriend}
        onBackHome={props.onBackHome}
        onOpenCatalog={props.onOpenCatalog}
        onOpenEditor={props.onOpenEditor}
        onOpenAbout={props.onOpenAbout}
        onExitGame={props.onExitGame}
        initialLobbyNavTab={props.initialLobbyNavTab}
        currentLobbyMode={props.currentLobbyMode}
        onLobbyNavTabChange={props.onLobbyNavTabChange}
      />
    );
  }

  return <HomeScreen {...props} />;
}

type HomeScreenProps = Props;

function HomeScreen(p: HomeScreenProps) {
  const {
    selectedMapTitle,
    selectedMapKind,
    mapStatus,
    publishedMapsCount,
    publishedMaps,
    myMaps,
    myMapsStatus,
    profile,
    profileStatus,
    leaderboard,
    friends,
    friendsStatus,
    onFindGame,
    onCreateGame,
    onOpenAbout,
    onOpenCatalog,
    onOpenEditor,
    onOpenLocalPreview,
    onQuickStart,
    onSelectBuiltin,
    onSelectCustomMap,
    onExitGame,
    canResumePausedP2p = false,
    onResumeP2pSession,
    selectedMapId,
    wanderBotsPreference = MYSTERY_ROOM_DEFAULT_WANDER_BOTS,
    onWanderBotsPreferenceChange,
  } = p;
  const storedUser = useStoredMenuUser();
  const isAuthenticated = storedUser !== null;
  const displayFriends = buildReferenceFriends(friends);
  const profileName = resolveMenuUserName(storedUser, profile, profileStatus);
  const profileAvatar = resolveMenuAvatar(storedUser);
  const profileInitial = profileName.trim().slice(0, 1).toUpperCase() || '?';
  const dailyTasks = buildDailyTasks(profile);
  const topCatalogMap = publishedMaps[0] ?? myMaps[0] ?? null;
  const topPlayer = leaderboard[0] ?? null;
  const moneyLabel = profile ? formatCompactNumber(profile.money) : '—';
  const ratingLabel = profile ? formatCompactNumber(profile.rating) : '—';
  const [modesOpen, setModesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TopTabKey>('profile');
  const [missionBriefOpen, setMissionBriefOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<{ author: string; text: string }[]>(() => [
    { author: 'Система', text: 'Добро пожаловать в Mystery Room!' },
  ]);

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatMessages((prev) => [...prev, { author: profileName, text }]);
    setChatInput('');
  };

  const shellRef = useRef<HTMLDivElement>(null);

  const openAuth = () => {
    if (typeof window === 'undefined') return;
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = `/login?return_to=${encodeURIComponent(returnTo)}`;
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (missionBriefOpen) {
          setMissionBriefOpen(false);
          return;
        }
        setActiveTab('profile');
        setModesOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [missionBriefOpen]);

  useEffect(() => {
    const root = shellRef.current;
    if (!root || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onMove = (e: PointerEvent) => {
      const r = root.getBoundingClientRect();
      const x = ((e.clientX - r.left) / Math.max(r.width, 1) - 0.5) * 2;
      const y = ((e.clientY - r.top) / Math.max(r.height, 1) - 0.5) * 2;
      root.style.setProperty('--mr-parallax-x', x.toFixed(4));
      root.style.setProperty('--mr-parallax-y', y.toFixed(4));
    };
    const onLeave = () => {
      root.style.setProperty('--mr-parallax-x', '0');
      root.style.setProperty('--mr-parallax-y', '0');
    };
    root.addEventListener('pointermove', onMove);
    root.addEventListener('pointerleave', onLeave);
    return () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <section className="mr-ref-menu min-h-0 flex-1 overflow-y-auto text-site-text">
      <div ref={shellRef} className="mr-ref-shell mr-ref-shell--parallax">
        <div className="mr-ref-parallax-bg" aria-hidden="true">
          <img src={MENU_LAB_BG_URL} alt="" />
        </div>
        <div className="mr-ref-parallax-hero" aria-hidden="true">
          <img src={MENU_HERO_FULL_URL} alt="" />
        </div>
        <ReferenceTopBar
          moneyLabel={moneyLabel}
          ratingLabel={ratingLabel}
          activeTab={activeTab}
          onSelectTab={setActiveTab}
          onOpenAbout={onOpenAbout}
          onCreateGame={onCreateGame}
          onOpenLobby={onCreateGame}
          onExitGame={onExitGame}
        />

        <main className="mr-ref-content" aria-label="Главное меню Mystery Room">
          <aside className="mr-ref-left">
            <section className="mr-ref-panel mr-ref-profile-card">
              {!isAuthenticated ? (
                <>
                  <div className="mr-ref-avatar-photo mr-ref-avatar-fallback" aria-hidden="true">?</div>
                  <div className="min-w-0 flex-1">
                    <div className="mr-ref-profile-name">Гость</div>
                    <div className="mr-ref-profile-role">Войдите, чтобы сохранить прогресс и играть в лобби</div>
                    <button type="button" onClick={openAuth} className="mr-ref-outline-button mt-4 w-full">
                      ВОЙТИ В АККАУНТ
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {profileAvatar ? (
                    <img src={profileAvatar} alt="" className="mr-ref-avatar-photo" />
                  ) : (
                    <div className="mr-ref-avatar-photo mr-ref-avatar-fallback" aria-hidden="true">
                      {profileInitial}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mr-ref-profile-name">{profileName}</div>
                    <div className="mr-ref-profile-role">{profile ? `MMR ${formatCompactNumber(profile.rating)}` : profileStatus}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="mr-ref-xp-current">{profile ? `${profile.matchesPlayed} матчей` : 'Нет профиля'}</span>
                      {profile && <span className="mr-ref-muted">/ {profile.defenderWins} побед защиты</span>}
                    </div>
                    <div className="mr-ref-progress mt-2">
                      <span style={{ width: `${profileProgress(profile)}%` }} />
                    </div>
                  </div>
                  <div className="mr-ref-level-badge">
                    <span>{profile ? profile.rating : '—'}</span>
                  </div>
                </>
              )}
            </section>

            <section className="mr-ref-panel mr-ref-task-card">
              <div className="mr-ref-panel-head">
                <span>ЕЖЕДНЕВНЫЕ ЗАДАЧИ</span>
                <span>{dailyTasks.length}/3</span>
              </div>
              <div className="mt-4 grid gap-2">
                {dailyTasks.map((task) => (
                  <div key={task.label} className="mr-ref-daily-row">
                    <div className="truncate text-[0.78rem] font-semibold uppercase tracking-[0.04em] text-site-text">{task.label}</div>
                    <div className="text-[0.78rem] font-semibold text-site-secondary">
                      {task.current} / {task.target}
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={onOpenCatalog} className="mr-ref-outline-button mt-7 w-full">
                ВСЕ ЗАДАЧИ
              </button>
            </section>

            <section className="mr-ref-panel mr-ref-friends-card">
              <div className="mr-ref-panel-head">
                <span>ДРУЗЬЯ В СЕТИ</span>
                <span>{friends.length}</span>
              </div>
              {displayFriends.length > 0 ? (
                <div className="mt-4 grid gap-2">
                  {displayFriends.map((friend, index) => (
                    <div key={`${friend.name}-${index}`} className="mr-ref-friend-row">
                      {friend.avatarUrl ? (
                        <img src={friend.avatarUrl} alt="" className="mr-ref-friend-avatar" />
                      ) : (
                        <span className="mr-ref-friend-avatar mr-ref-friend-initial" aria-hidden="true">
                          {friend.name.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[0.83rem] font-semibold text-site-text">{friend.name}</div>
                        <div className={`truncate text-[0.74rem] font-medium mr-ref-status-${friend.tone}`}>{friend.status}</div>
                      </div>
                      {friend.joinUrl ? (
                        <a
                          href={friend.joinUrl}
                          className="shrink-0 rounded-[8px] border border-site-success/35 bg-site-success/12 px-2 py-1 text-[0.65rem] font-bold uppercase tracking-[0.06em] text-site-success hover:bg-site-success/22"
                        >
                          В лобби
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mr-ref-empty-state mt-5">{friendsStatus}</div>
              )}
              <button type="button" onClick={onOpenAbout} className="mr-ref-outline-button mt-4 w-full">
                ВСЕ ДРУЗЬЯ
              </button>
            </section>

            <div className="mr-ref-bottom-tools" aria-label="Быстрые действия">
              <button type="button" onClick={onOpenAbout} aria-label="Чат">
                <span>💬</span>
              </button>
              <button type="button" onClick={onOpenAbout} aria-label="Друзья">
                <span>👥</span>
              </button>
              <button type="button" onClick={onOpenCatalog} aria-label="Поиск игры">
                <span>🔍</span>
              </button>
              <button type="button" onClick={onOpenEditor} aria-label="Настройки">
                <span>⚙</span>
              </button>
            </div>
          </aside>

          <section className="mr-ref-center" aria-label="Рекомендуемая миссия">
            <div className="mr-ref-mission-card">
              <div className="mr-ref-section-label">РЕКОМЕНДУЕМАЯ МИССИЯ</div>
              <h1>{selectedMapTitle}</h1>
              <p>{mapStatus}</p>
              <div className="mr-ref-section-label mt-4">НАГРАДЫ</div>
              <div className="mt-2 flex flex-wrap items-center gap-5">
                <span className="mr-ref-reward mr-ref-reward-xp">XP&nbsp;&nbsp;8500</span>
                <span className="mr-ref-reward mr-ref-reward-coin">₽&nbsp;&nbsp;7 500</span>
              </div>
              <button
                type="button"
                title={`Текущая карта: ${selectedMapTitle}`}
                onClick={() => setMissionBriefOpen(true)}
                className="mr-ref-details-button mt-5"
              >
                ПОДРОБНОСТИ МИССИИ
              </button>
            </div>

            <div className="mr-ref-chat" aria-label="Чат">
              <div className="mr-ref-chat-head">ЧАТ</div>
              <div className="mr-ref-chat-messages">
                {chatMessages.map((msg, i) => (
                  <div key={i} className="mr-ref-chat-msg">
                    <span className="mr-ref-chat-author">{msg.author}:</span> {msg.text}
                  </div>
                ))}
              </div>
              <form
                className="mr-ref-chat-input-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendChatMessage();
                }}
              >
                <input
                  type="text"
                  className="mr-ref-chat-input"
                  placeholder="Введите сообщение..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                />
                <button type="submit" className="mr-ref-chat-send" aria-label="Отправить">
                  ➤
                </button>
              </form>
            </div>
          </section>

          <aside className="mr-ref-right">
            <section className="mr-ref-media-card mr-ref-event-card">
              <div className="mr-ref-media-overlay">
                <div className="mr-ref-section-label">СОБЫТИЕ</div>
                <div className="mt-3 text-[0.92rem] font-semibold uppercase text-site-text">Операция: Ночной Шторм</div>
                <div className="text-[0.82rem] text-site-secondary">Ограниченное событие</div>
                <div className="mt-2 text-[1rem] font-semibold text-site-warning">{mapStatus}</div>
              </div>
            </section>

            <section className="mr-ref-media-card mr-ref-news-card">
              <div className="mr-ref-media-overlay">
                <div className="mr-ref-section-label">НОВОСТИ</div>
                <div className="mt-3 text-[0.92rem] font-semibold uppercase text-site-text">
                  {topCatalogMap ? topCatalogMap.title : 'Новое снаряжение'}
                </div>
                <div className="text-[0.82rem] text-site-secondary">{topCatalogMap ? topCatalogMap.description || formatMapStats(topCatalogMap) : 'Уже в продаже'}</div>
              </div>
            </section>

            <section className="mr-ref-panel mr-ref-pass-card">
              <div>
                <div className="mr-ref-section-label">СЕЗОННЫЙ ПРОПУСК</div>
                <div className="mt-6 flex items-end gap-6">
                  <span className="text-[1rem] uppercase text-site-muted">УР.</span>
                  <span className="mr-ref-pass-level">{ratingLabel}</span>
                  <span className="pb-2 text-[0.7rem] text-site-muted">{topPlayer ? `${topPlayer.rating} / 10 000` : 'лидерборд'}</span>
                </div>
                <div className="mr-ref-progress mt-3 max-w-[195px]">
                  <span style={{ width: `${profileProgress(profile)}%` }} />
                </div>
              </div>
              <div className="mr-ref-pass-medal mr-ref-pass-medal-live" aria-hidden="true">
                {profile ? profile.matchesPlayed : '—'}
              </div>
              <button type="button" onClick={onOpenCatalog} className="mr-ref-outline-button mt-6 w-full">
                ОТКРЫТЬ ПРОПУСК
              </button>
            </section>

            <div className="mr-ref-play-wrapper">
              {canResumePausedP2p && onResumeP2pSession ? (
                <button
                  type="button"
                  onClick={onResumeP2pSession}
                  className="mb-3 w-full rounded-2xl border border-site-accent/45 bg-site-accent/15 px-4 py-3 text-[0.72rem] font-black uppercase tracking-[0.14em] text-site-accent shadow-[0_12px_40px_rgba(6,182,212,0.12)] transition hover:border-site-accent/70 hover:bg-site-accent/25"
                >
                  Вернуться в смену
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setModesOpen((v) => !v)}
                className="mr-ref-play-button"
                aria-expanded={modesOpen}
                aria-controls="mr-ref-modes-panel"
              >
                <span>ИГРАТЬ</span>
                <small>ВЫБРАТЬ РЕЖИМ</small>
              </button>

              {modesOpen && (
                <div id="mr-ref-modes-panel" className="mr-ref-modes">
                  <div className="mb-3 w-full px-0.5">
                    <WanderBotsStepper
                      value={wanderBotsPreference}
                      onChange={(n) => onWanderBotsPreferenceChange?.(clampWanderBotCount(n))}
                      label="Боты (одиночка / хост P2P)"
                    />
                  </div>
                  <button type="button" className="mr-ref-mode-btn" onClick={() => { setModesOpen(false); onFindGame(); }}>
                    <span className="mr-ref-mode-icon">⚔</span>
                    <div>
                      <div className="mr-ref-mode-title">Сетевая игра</div>
                      <div className="mr-ref-mode-desc">Найти матч с игроками</div>
                    </div>
                  </button>
                  <button type="button" className="mr-ref-mode-btn" onClick={() => { setModesOpen(false); onCreateGame(); }}>
                    <span className="mr-ref-mode-icon">🛡</span>
                    <div>
                      <div className="mr-ref-mode-title">Создать лобби</div>
                      <div className="mr-ref-mode-desc">Пригласить друзей</div>
                    </div>
                  </button>
                  <button type="button" className="mr-ref-mode-btn" onClick={() => { setModesOpen(false); onQuickStart(); }}>
                    <span className="mr-ref-mode-icon">⚡</span>
                    <div>
                      <div className="mr-ref-mode-title">Быстрый старт</div>
                      <div className="mr-ref-mode-desc">Локальная сессия</div>
                    </div>
                  </button>
                  <button type="button" className="mr-ref-mode-btn" onClick={() => { setModesOpen(false); onOpenLocalPreview(); }}>
                    <span className="mr-ref-mode-icon">🔬</span>
                    <div>
                      <div className="mr-ref-mode-title">Тестовый режим</div>
                      <div className="mr-ref-mode-desc">Без подключения к серверу</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </aside>

          {activeTab !== 'profile' && (
            <TabOverlay
              tab={activeTab}
              onClose={() => setActiveTab('profile')}
              profile={profile}
              friends={friends}
              friendsStatus={friendsStatus}
              leaderboard={leaderboard}
              publishedMaps={publishedMaps}
              myMaps={myMaps}
              myMapsStatus={myMapsStatus}
              dailyTasks={dailyTasks}
              onSelectBuiltin={() => { onSelectBuiltin(); setActiveTab('profile'); }}
              onSelectCustomMap={(id) => { onSelectCustomMap(id); setActiveTab('profile'); }}
              onOpenEditor={onOpenEditor}
              onOpenCatalog={onOpenCatalog}
              onCreateGame={onCreateGame}
              onFindGame={onFindGame}
            />
          )}
        {missionBriefOpen ? (
          <MissionBriefOverlay
            selectedMapTitle={selectedMapTitle}
            selectedMapId={selectedMapId}
            selectedMapKind={selectedMapKind}
            mapStatus={mapStatus}
            publishedMaps={publishedMaps}
            myMaps={myMaps}
            onQuickStart={() => {
              setModesOpen(false);
              onQuickStart();
            }}
            onFindGame={() => {
              setModesOpen(false);
              onFindGame();
            }}
            onCreateGame={() => {
              setModesOpen(false);
              onCreateGame();
            }}
            onOpenCatalog={onOpenCatalog}
            onClose={() => setMissionBriefOpen(false)}
            wanderBotsPreference={wanderBotsPreference}
            onWanderBotsPreferenceChange={onWanderBotsPreferenceChange}
          />
        ) : null}
        </main>
      </div>
    </section>
  );
}

function ReferenceTopBar({
  moneyLabel,
  ratingLabel,
  activeTab,
  onSelectTab,
  onOpenAbout,
  onCreateGame,
  onOpenLobby,
  onExitGame,
}: {
  moneyLabel: string;
  ratingLabel: string;
  activeTab: TopTabKey;
  onSelectTab: (t: TopTabKey) => void;
  onOpenAbout: () => void;
  onCreateGame: () => void;
  /** Переход на экран настройки лобби (оранжевая кнопка в нав.) */
  onOpenLobby: () => void;
  onExitGame: () => void;
}) {
  return (
    <header className="mr-ref-topbar">
      <button type="button" onClick={() => onSelectTab('profile')} className="mr-ref-logo-cell" aria-label="Mystery Room">
        <span className="mr-ref-logo-mark" aria-hidden="true" />
      </button>
      <nav className="mr-ref-nav" aria-label="Разделы меню">
        {TOP_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onSelectTab(tab.key)}
            className={`mr-ref-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            aria-current={activeTab === tab.key ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
        <button type="button" onClick={onOpenLobby} className="mr-ref-nav-item mr-ref-nav-item--lobby shrink-0" aria-label="Лобби">
          ЛОББИ
        </button>
      </nav>
      <div className="mr-ref-top-actions">
        <span className="mr-ref-currency" title="Игровые деньги">
          <span className="mr-ref-currency-coin" aria-hidden="true" />
          {moneyLabel}
        </span>
        <span className="mr-ref-currency" title="Рейтинг">
          <span className="mr-ref-currency-crystal" aria-hidden="true" />
          {ratingLabel}
        </span>
        <button type="button" onClick={onOpenAbout} aria-label="Почта" title="Почта" className="mr-ref-top-icon">
          ✉
        </button>
        <button type="button" onClick={onCreateGame} aria-label="Друзья" title="Друзья" className="mr-ref-top-icon">
          👥
        </button>
        <button type="button" onClick={() => onSelectTab('shop')} aria-label="Настройки" title="Настройки" className="mr-ref-top-icon">
          ⚙
        </button>
        <button type="button" onClick={onExitGame} aria-label="Выход" title="Выход" className="mr-ref-top-icon">
          ⏻
        </button>
      </div>
    </header>
  );
}

function TabOverlay({
  tab,
  onClose,
  profile,
  friends,
  friendsStatus,
  leaderboard,
  publishedMaps,
  myMaps,
  myMapsStatus,
  dailyTasks,
  onSelectBuiltin,
  onSelectCustomMap,
  onOpenEditor,
  onOpenCatalog,
  onCreateGame,
  onFindGame,
}: {
  tab: Exclude<TopTabKey, 'profile'>;
  onClose: () => void;
  profile: MrProfileResponse['profile'] | null;
  friends: MrFriend[];
  friendsStatus: string;
  leaderboard: MrStatsPlayer[];
  publishedMaps: MrMapSummary[];
  myMaps: MrMapSummary[];
  myMapsStatus: string;
  dailyTasks: { label: string; current: number; target: number }[];
  onSelectBuiltin: () => void;
  onSelectCustomMap: (id: string) => void;
  onOpenEditor: () => void;
  onOpenCatalog: () => void;
  onCreateGame: () => void;
  onFindGame: () => void;
}) {
  const titleByTab: Record<typeof tab, string> = {
    missions: 'МИССИИ',
    gear: 'СНАРЯЖЕНИЕ',
    database: 'БАЗА ДАННЫХ',
    contracts: 'КОНТРАКТЫ',
    shop: 'МАГАЗИН',
  };
  return (
    <div className="mr-ref-overlay" role="dialog" aria-modal="true" aria-label={titleByTab[tab]}>
      <div className="mr-ref-overlay-head">
        <span className="mr-ref-overlay-title">{titleByTab[tab]}</span>
        <button type="button" onClick={onClose} className="mr-ref-overlay-close" aria-label="Закрыть">
          ×
        </button>
      </div>
      <div className="mr-ref-overlay-body">
        {tab === 'missions' && (
          <MissionsTab
            publishedMaps={publishedMaps}
            myMaps={myMaps}
            myMapsStatus={myMapsStatus}
            onSelectBuiltin={onSelectBuiltin}
            onSelectCustomMap={onSelectCustomMap}
            onOpenEditor={onOpenEditor}
            onOpenCatalog={onOpenCatalog}
          />
        )}
        {tab === 'gear' && <GearTab />}
        {tab === 'database' && (
          <DatabaseTab profile={profile} leaderboard={leaderboard} friends={friends} friendsStatus={friendsStatus} />
        )}
        {tab === 'contracts' && (
          <ContractsTab dailyTasks={dailyTasks} onCreateGame={onCreateGame} onFindGame={onFindGame} />
        )}
        {tab === 'shop' && <ShopTab money={profile?.money ?? 0} />}
      </div>
    </div>
  );
}

function MissionsTab({
  publishedMaps,
  myMaps,
  myMapsStatus,
  onSelectBuiltin,
  onSelectCustomMap,
  onOpenEditor,
  onOpenCatalog,
}: {
  publishedMaps: MrMapSummary[];
  myMaps: MrMapSummary[];
  myMapsStatus: string;
  onSelectBuiltin: () => void;
  onSelectCustomMap: (id: string) => void;
  onOpenEditor: () => void;
  onOpenCatalog: () => void;
}) {
  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onOpenCatalog} className="mr-ref-outline-button px-4">КАТАЛОГ</button>
        <button type="button" onClick={onOpenEditor} className="mr-ref-outline-button px-4">РЕДАКТОР КАРТ</button>
      </div>
      <div className="mr-ref-overlay-grid">
        <button type="button" className="mr-ref-tile" onClick={onSelectBuiltin}>
          <div className="mr-ref-tile-title">LISAD-9: Night Shift</div>
          <div className="mr-ref-tile-desc">Встроенная карта для быстрого старта.</div>
          <div className="mr-ref-tile-meta"><span>BUILTIN</span><span>4–6 ИГРОКОВ</span></div>
        </button>
        {publishedMaps.map((map) => (
          <button key={map.id} type="button" className="mr-ref-tile" onClick={() => onSelectCustomMap(map.id)}>
            <div className="mr-ref-tile-title">{map.title}</div>
            <div className="mr-ref-tile-desc">{map.description || `Автор: ${map.ownerName || `user #${map.ownerUserId}`}`}</div>
            <div className="mr-ref-tile-meta">
              <span>{formatMapStats(map)}</span>
              {map.activeMatches ? <span>{map.activeMatches} LIVE</span> : null}
            </div>
          </button>
        ))}
        {myMaps.map((map) => (
          <button key={`my-${map.id}`} type="button" className="mr-ref-tile" onClick={() => onSelectCustomMap(map.id)}>
            <div className="mr-ref-tile-title">{map.title}</div>
            <div className="mr-ref-tile-desc">{map.description || 'Своя карта'}</div>
            <div className="mr-ref-tile-meta"><span>МОЯ</span><span>{map.status}</span></div>
          </button>
        ))}
        {publishedMaps.length === 0 && myMaps.length === 0 && (
          <div className="mr-ref-empty-state">{myMapsStatus}</div>
        )}
      </div>
    </div>
  );
}

function GearTab() {
  return (
    <div className="grid gap-6">
      <div className="mr-ref-overlay-grid">
        {HEROES.map((hero) => (
          <div key={hero.id} className="mr-ref-tile">
            <div className="mr-ref-tile-title">{hero.name}</div>
            <div className="mr-ref-tile-desc">{hero.role}</div>
            <div className="mr-ref-tile-meta">
              <span>{hero.status}</span>
              {hero.price > 0 ? <span>{formatMoney(hero.price)}</span> : <span>ДОСТУПЕН</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DatabaseTab({
  profile,
  leaderboard,
  friends,
  friendsStatus,
}: {
  profile: MrProfileResponse['profile'] | null;
  leaderboard: MrStatsPlayer[];
  friends: MrFriend[];
  friendsStatus: string;
}) {
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="mr-ref-panel" style={{ padding: 18 }}>
        <div className="mr-ref-panel-head"><span>СТАТИСТИКА</span><span>{profile ? `${profile.matchesPlayed} матчей` : '—'}</span></div>
        {profile ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Metric label="Контракты" value={profile.crimesCompleted} />
            <Metric label="Раскрыто" value={profile.crimesExposed} />
            <Metric label="Победы защиты" value={profile.defenderWins} />
            <Metric label="MMR" value={profile.rating} />
          </div>
        ) : (
          <div className="mr-ref-empty-state mt-4">Нет данных профиля</div>
        )}
      </section>
      <section className="mr-ref-panel" style={{ padding: 18 }}>
        <div className="mr-ref-panel-head"><span>РЕЙТИНГ</span><span>{leaderboard.length}</span></div>
        <div className="mt-3 grid gap-2">
          {leaderboard.length > 0 ? (
            leaderboard.slice(0, 8).map((p) => (
              <div key={p.userId} className="flex items-center justify-between gap-3 border-b border-site-border py-2">
                <span className="text-[0.82rem] text-site-text">#{p.rank} {p.name}</span>
                <span className="text-[0.82rem] font-semibold text-site-warning">{p.rating}</span>
              </div>
            ))
          ) : (
            <div className="mr-ref-empty-state">Лидерборд пуст</div>
          )}
        </div>
      </section>
      <section className="mr-ref-panel xl:col-span-2" style={{ padding: 18 }}>
        <div className="mr-ref-panel-head"><span>ДРУЗЬЯ</span><span>{friends.length}</span></div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {friends.length > 0 ? sortMysteryRoomFriends(friends).slice(0, 12).map((f) => {
            const name = f.display_name || f.username || `user #${f.id}`;
            return (
              <div key={f.id} className="flex items-center gap-3 border border-site-border bg-site-surface/[0.03] px-3 py-2">
                {f.avatar_url ? <img src={f.avatar_url} alt="" className="size-8 object-cover" /> : <span className="grid size-8 place-items-center bg-site-accent/14 text-xs text-site-accent">{name.slice(0, 1).toUpperCase()}</span>}
                <div className="min-w-0">
                  <div className="truncate text-[0.82rem] text-site-text">{name}</div>
                  <div className="truncate text-[0.7rem] text-site-muted">{formatMysteryRoomFriendPresence(f)}</div>
                </div>
              </div>
            );
          }) : <div className="mr-ref-empty-state">{friendsStatus}</div>}
        </div>
      </section>
    </div>
  );
}

function ContractsTab({ dailyTasks, onCreateGame, onFindGame }: { dailyTasks: { label: string; current: number; target: number }[]; onCreateGame: () => void; onFindGame: () => void }) {
  return (
    <div className="grid gap-6">
      <section className="mr-ref-panel" style={{ padding: 18 }}>
        <div className="mr-ref-panel-head"><span>ЕЖЕДНЕВНЫЕ КОНТРАКТЫ</span><span>{dailyTasks.length}/3</span></div>
        <div className="mt-4 grid gap-3">
          {dailyTasks.map((task) => (
            <div key={task.label} className="grid gap-2 border border-site-border bg-site-surface/[0.025] p-3">
              <div className="flex items-center justify-between">
                <span className="text-[0.86rem] font-semibold uppercase text-site-text">{task.label}</span>
                <span className="text-[0.82rem] font-semibold text-site-warning">{task.current} / {task.target}</span>
              </div>
              <div className="mr-ref-task-progress">
                <span style={{ width: `${Math.min(100, Math.round((task.current / Math.max(1, task.target)) * 100))}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>
      <div className="flex flex-wrap gap-3">
        <button type="button" className="mr-ref-outline-button px-5" onClick={onFindGame}>НАЙТИ ИГРУ</button>
        <button type="button" className="mr-ref-outline-button px-5" onClick={onCreateGame}>СОЗДАТЬ ЛОББИ</button>
      </div>
    </div>
  );
}

function ShopTab({ money }: { money: number }) {
  return (
    <div className="grid gap-6">
      <div className="text-[0.82rem] text-site-muted">Доступно: <span className="text-site-warning font-semibold">{formatMoney(money)}</span></div>
      <div className="mr-ref-overlay-grid">
        {STORE_ITEMS.map((item) => {
          const canBuy = money >= item.price;
          return (
            <div key={item.id} className="mr-ref-tile">
              <div className="mr-ref-tile-title">{item.title}</div>
              <div className="mr-ref-tile-desc">{item.detail}</div>
              <div className="mr-ref-tile-meta">
                <span>{formatMoney(item.price)}</span>
                <span style={{ color: canBuy ? '#7dd3a4' : '#a1a1aa' }}>{canBuy ? 'ДОСТУПНО' : 'НЕХВАТКА'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="border border-site-border bg-site-surface/[0.03] p-3">
      <div className="text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">{label}</div>
      <div className="mt-1 text-lg font-black text-site-text">{formatCompactNumber(value)}</div>
    </div>
  );
}


function AboutScreen(props: Props) {
  return (
    <section className="min-h-0 flex-1 overflow-y-auto bg-site-surface text-site-text">
      <div className="relative isolate min-h-full overflow-hidden pb-32">
        <MenuBackdrop motion />
      <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-5 sm:px-6">
        <MenuPanel eyebrow="об игре" title="Мир Mystery Room" action={<SmallButton onClick={props.onBackHome}>Назад</SmallButton>}>
          <div className="grid gap-4 lg:grid-cols-3">
            <InfoPanel title="LISAD-9" eyebrow="place">
              Изолированная лаборатория с пропусками, серверной, реактором, аварийными системами и скрытыми маршрутами.
            </InfoPanel>
            <InfoPanel title="Скрытая операция" eyebrow="mystery">
              Один участник выполняет контракт, остальные чинят инциденты, собирают улики и пытаются раскрыть преступника.
            </InfoPanel>
            <InfoPanel title="Командное давление" eyebrow="play">
              Смена завершается победителем, наградами, рейтингом и статистикой по задачам.
            </InfoPanel>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <SmallButton onClick={props.onFindGame}>Начать игру</SmallButton>
            <SmallButton onClick={props.onCreateGame}>Создать игру</SmallButton>
          </div>
        </MenuPanel>
      </div>
        <FloatingFriendsDock
          friends={props.friends}
          friendsStatus={props.friendsStatus}
          compact={false}
          inviteStatusByFriendId={props.inviteStatusByFriendId}
          onInviteFriend={props.onInviteFriend}
        />
      </div>
    </section>
  );
}

function PlayTab({
  selectedMapTitle,
  selectedMapId,
  selectedMapDefinition,
  mapStatus,
  mapsAuthRequired,
  mapsCatalogLoaded,
  publishedMapsCount,
  publishedMaps,
  profile,
  onOpenCatalog,
  onSelectBuiltin,
  onSelectCustomMap,
}: Pick<
  Props,
  | 'selectedMapTitle'
  | 'selectedMapId'
  | 'selectedMapDefinition'
  | 'mapStatus'
  | 'mapsAuthRequired'
  | 'mapsCatalogLoaded'
  | 'publishedMapsCount'
  | 'publishedMaps'
  | 'profile'
  | 'onOpenCatalog'
  | 'onSelectBuiltin'
  | 'onSelectCustomMap'
>) {
  const topPublishedMaps = publishedMaps.slice(0, 6);
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_410px]">
      <MenuPanel
        eyebrow="раздел игра"
        title="Карта и режим запуска"
        action={<SmallButton onClick={onOpenCatalog}>{publishedMapsCount > 0 ? `Каталог: ${publishedMapsCount}` : 'Каталог'}</SmallButton>}
      >
        <div className="grid gap-4 2xl:grid-cols-[360px_minmax(0,1fr)]">
          <MysteryRoomMapCard
            title="LISAD-9: Night Shift"
            subtitle="Встроенная карта"
            description="Базовая лаборатория для быстрого старта, лобби и теста механик."
            selected={selectedMapId === BUILTIN_MAP_ID}
            badge="default"
            tone="cyan"
            mapDefinition={selectedMapId === BUILTIN_MAP_ID ? selectedMapDefinition : undefined}
            onClick={onSelectBuiltin}
          />
          <div className="grid max-h-[444px] gap-3 overflow-y-auto pr-1 md:grid-cols-2 2xl:grid-cols-3">
            {mapsAuthRequired ? (
              <EmptyState title="Нужен вход" detail="Войдите в аккаунт, чтобы выбирать опубликованные карты игроков." />
            ) : topPublishedMaps.length > 0 ? (
              topPublishedMaps.map((map) => (
                <MysteryRoomMapCard
                  key={map.id}
                  title={map.title}
                  subtitle={`${map.ownerName || `user #${map.ownerUserId}`} · ${formatMapStats(map)}`}
                  description={map.description || `Обновлена ${formatDate(map.updatedAt)}`}
                  selected={selectedMapId === map.id}
                  badge={map.activeMatches ? `${map.activeMatches} live` : 'published'}
                  preview={map.preview}
                  onClick={() => onSelectCustomMap(map.id)}
                />
              ))
            ) : (
              <EmptyState
                title={mapsCatalogLoaded ? 'Опубликованных карт пока нет' : 'Загружаю карты'}
                detail={mapsCatalogLoaded ? 'Создайте и опубликуйте первую карту в редакторе.' : 'Каталог появится через несколько секунд.'}
              />
            )}
          </div>
        </div>
        <div className="mt-4 grid gap-2 text-xs text-site-muted sm:grid-cols-2">
          <StatusLine label="Текущая карта" value={selectedMapTitle} />
          <StatusLine label="Состояние каталога" value={mapStatus} />
        </div>
      </MenuPanel>
      <HeroPreview selectedMapTitle={selectedMapTitle} profile={profile} />
    </div>
  );
}

function TabButton({ active, label, detail, icon, onClick }: { active: boolean; label: string; detail: string; icon?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-[72px] rounded-[18px] border px-4 py-3 text-left transition active:scale-[0.99] ${
        active
          ? 'border-site-accent/50 bg-site-accent text-site-on-accent shadow-[0_16px_34px_rgba(34,211,238,0.18)]'
          : 'border-site-border bg-site-surface/[0.045] text-site-text hover:border-site-accent/24 hover:bg-site-surface/[0.075]'
      }`}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="text-base font-black">{label}</span>
        {icon && <span className="grid size-8 place-items-center rounded-full border border-current/20 text-lg leading-none">{icon}</span>}
      </span>
      <span className={`mt-1 block truncate text-xs font-semibold ${active ? 'text-site-muted/72' : 'text-site-muted'}`}>{detail}</span>
    </button>
  );
}

function StartActionsDock({
  selectedMapTitle,
  onFindGame,
  onQuickStart,
  onOpenCatalog,
  onOpenLocalPreview,
  onOpenEditor,
  onOpenAbout,
  onExitGame,
}: {
  selectedMapTitle: string;
  onFindGame: () => void;
  onQuickStart: () => void;
  onOpenCatalog: () => void;
  onOpenLocalPreview: () => void;
  onOpenEditor: () => void;
  onOpenAbout: () => void;
  onExitGame: () => void;
}) {
  return (
    <aside className="relative z-30 mx-4 mb-4 w-auto rounded-[24px] border border-site-accent/24 bg-site-bg/88 p-3 shadow-[0_24px_80px_rgba(2,6,23,0.62)] backdrop-blur-xl lg:fixed lg:bottom-4 lg:right-4 lg:mx-0 lg:mb-0 lg:w-[min(420px,calc(100vw-2rem))]">
      <div className="mb-3 min-w-0">
        <div className="text-[0.62rem] uppercase tracking-[0.22em] text-site-accent/62">начало игры</div>
        <div className="mt-1 truncate text-sm font-bold text-site-text">{selectedMapTitle}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onFindGame} className="min-h-[58px] rounded-[16px] border border-site-accent/45 bg-site-accent px-4 py-3 text-left font-black text-site-on-accent transition hover:bg-site-accent">
          Играть
        </button>
        <button type="button" onClick={onQuickStart} className="min-h-[58px] rounded-[16px] border border-site-warning/45 bg-site-warning px-4 py-3 text-left font-black text-site-on-accent transition hover:bg-site-warning">
          Быстрый старт
        </button>
        <button type="button" onClick={onOpenLocalPreview} className="rounded-[14px] border border-site-border bg-site-surface/[0.055] px-3 py-2 text-left text-xs font-bold text-site-text transition hover:border-site-border">
          Локальный тест
        </button>
        <button type="button" onClick={onOpenCatalog} className="rounded-[14px] border border-site-border bg-site-surface/[0.055] px-3 py-2 text-left text-xs font-bold text-site-text transition hover:border-site-border">
          Выбор карты
        </button>
        <button type="button" onClick={onOpenEditor} className="rounded-[14px] border border-site-border bg-site-surface/[0.055] px-3 py-2 text-left text-xs font-bold text-site-text transition hover:border-site-border">
          Редактор
        </button>
        <button type="button" onClick={onOpenAbout} className="rounded-[14px] border border-site-border bg-site-surface/[0.055] px-3 py-2 text-left text-xs font-bold text-site-text transition hover:border-site-border">
          Об игре
        </button>
        <button type="button" onClick={onExitGame} className="rounded-[14px] border border-site-danger/18 bg-site-danger/10 px-3 py-2 text-left text-xs font-bold text-site-danger transition hover:border-site-danger/36">
          Выйти
        </button>
      </div>
    </aside>
  );
}

function FloatingFriendsDock({
  friends,
  friendsStatus,
  compact,
  inviteStatusByFriendId,
  onInviteFriend,
}: {
  friends: MrFriend[];
  friendsStatus: string;
  compact: boolean;
  inviteStatusByFriendId: Record<number, string>;
  onInviteFriend: (friend: MrFriend) => void;
}) {
  return (
    <aside className="relative z-30 mx-4 mb-4 w-auto lg:fixed lg:bottom-4 lg:left-4 lg:mx-0 lg:mb-0 lg:w-[min(390px,calc(100vw-2rem))]">
      <FriendsPanel
        friends={friends}
        friendsStatus={friendsStatus}
        compact={compact}
        inviteStatusByFriendId={inviteStatusByFriendId}
        onInviteFriend={onInviteFriend}
        dock
      />
    </aside>
  );
}

function MenuBackdrop({ motion }: { motion: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(34,211,238,0.18),transparent_28%),radial-gradient(circle_at_82%_18%,rgba(251,191,36,0.12),transparent_26%),linear-gradient(180deg,#07111f_0%,#030712_58%,#02040a_100%)]" />
      <div className={`absolute left-[-12%] top-16 h-72 w-[48rem] rotate-[-9deg] bg-site-accent/12 blur-3xl ${motion ? 'animate-pulse' : ''}`} />
      <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/78 to-transparent" />
    </div>
  );
}

function HeroPreview({ selectedMapTitle, profile }: { selectedMapTitle: string; profile: MrProfileResponse['profile'] | null }) {
  return (
    <div className="relative min-h-[280px] overflow-hidden rounded-[24px] border border-site-border bg-site-bg/54">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_24%,rgba(103,232,249,0.2),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.18),rgba(2,6,23,0.92))]" />
      <div className="absolute inset-x-8 bottom-9 h-20 rounded-[50%] bg-site-accent/10 blur-xl" />
      <img
        src={MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.menuUrl}
        alt={MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.name}
        className="absolute bottom-0 left-1/2 h-[92%] -translate-x-1/2 object-contain drop-shadow-[0_32px_52px_rgba(0,0,0,0.7)]"
      />
      <div className="absolute left-4 top-4 rounded-[18px] border border-site-border bg-black/38 px-4 py-3 backdrop-blur">
        <div className="text-[0.6rem] uppercase tracking-[0.22em] text-site-accent/70">текущая смена</div>
        <div className="mt-1 max-w-[220px] truncate text-sm font-black text-site-text">{selectedMapTitle}</div>
      </div>
      <div className="absolute bottom-4 right-4 rounded-[18px] border border-site-warning/22 bg-black/44 px-4 py-3 text-right backdrop-blur">
        <div className="text-[0.6rem] uppercase tracking-[0.18em] text-site-warning/70">баланс</div>
        <div className="mt-1 text-lg font-black text-site-warning">{formatMoney(profile?.money ?? 0)}</div>
      </div>
    </div>
  );
}

function ProfilePanel({ profile, profileStatus }: { profile: MrProfileResponse['profile'] | null; profileStatus: string }) {
  return (
    <MenuPanel eyebrow="профиль" title="Деньги и прогресс">
      {profile ? (
        <div className="grid grid-cols-2 gap-3">
          <StatTile label="Деньги" value={formatMoney(profile.money)} tone="amber" />
          <StatTile label="MMR" value={String(profile.rating)} tone="cyan" />
          <StatTile label="Матчи" value={String(profile.matchesPlayed)} />
          <StatTile label="Победы защиты" value={String(profile.defenderWins)} />
        </div>
      ) : (
        <EmptyState title="Профиль недоступен" detail={profileStatus} />
      )}
    </MenuPanel>
  );
}

function HeroesPanel({ money }: { money: number }) {
  return (
    <MenuPanel eyebrow="персонажи" title="Герои">
      <div className="grid grid-cols-2 gap-3">
        {HEROES.map((hero) => (
          <button
            type="button"
            key={hero.id}
            disabled={hero.price > 0}
            className="group min-h-[154px] overflow-hidden rounded-[18px] border border-site-border bg-site-surface/[0.045] p-3 text-left transition hover:border-site-accent/28 hover:bg-site-surface/[0.07] disabled:cursor-not-allowed disabled:opacity-80"
          >
            <div className="relative h-20 overflow-hidden rounded-[14px] bg-site-bg">
              {hero.imageUrl ? (
                <img src={hero.imageUrl} alt="" className="mx-auto h-full object-contain drop-shadow-[0_12px_18px_rgba(0,0,0,0.5)]" />
              ) : (
                <AvatarSilhouette seed={hero.id} />
              )}
            </div>
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-black text-site-text">{hero.name}</div>
                <div className="mt-1 truncate text-xs text-site-muted">{hero.role}</div>
              </div>
              <span className="rounded-full border border-site-border bg-site-surface/[0.06] px-2 py-1 text-[0.58rem] font-bold uppercase tracking-[0.12em] text-site-secondary">
                {hero.price ? formatMoney(hero.price) : hero.status}
              </span>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-3 text-xs text-site-muted">Доступно для покупок: {formatMoney(money)}</div>
    </MenuPanel>
  );
}

function StorePanel({ money }: { money: number }) {
  return (
    <MenuPanel eyebrow="магазин" title="На что тратить деньги">
      <div className="grid gap-2">
        {STORE_ITEMS.map((item) => {
          const canBuy = money >= item.price;
          return (
            <div key={item.id} className="flex items-center gap-3 rounded-[16px] border border-site-border bg-site-surface/[0.04] px-3 py-3">
              <div className={`size-10 rounded-[12px] ${storeToneClass(item.tone)}`} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-site-text">{item.title}</div>
                <div className="mt-0.5 truncate text-xs text-site-muted">{item.detail}</div>
              </div>
              <button
                type="button"
                disabled={!canBuy}
                className="rounded-full border border-site-border bg-site-surface/[0.05] px-3 py-1.5 text-xs font-bold text-site-text transition hover:border-site-warning/35 disabled:opacity-45"
              >
                {formatMoney(item.price)}
              </button>
            </div>
          );
        })}
      </div>
    </MenuPanel>
  );
}

function StatsPanel({ profile, leaderboard }: { profile: MrProfileResponse['profile'] | null; leaderboard: MrStatsPlayer[] }) {
  return (
    <MenuPanel eyebrow="статистика" title="Игры и рейтинг">
      <div className="grid grid-cols-3 gap-2">
        <MiniMetric value={profile?.crimesCompleted ?? 0} label="контракты" />
        <MiniMetric value={profile?.crimesExposed ?? 0} label="раскрыто" />
        <MiniMetric value={profile?.matchesPlayed ?? 0} label="матчи" />
      </div>
      <div className="mt-4 grid gap-2">
        {leaderboard.length > 0 ? (
          leaderboard.slice(0, 5).map((player) => (
            <div key={player.userId} className="flex items-center gap-3 rounded-[14px] border border-site-border bg-site-surface/[0.035] px-3 py-2">
              <div className="grid size-8 place-items-center overflow-hidden rounded-full bg-site-accent/14 text-xs font-black text-site-accent">
                {player.avatar ? <img src={player.avatar} alt="" className="h-full w-full object-cover" /> : player.rank}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-site-text">{player.name}</div>
                <div className="text-xs text-site-muted">{player.matchesPlayed} матчей</div>
              </div>
              <div className="text-sm font-black text-site-accent">{player.rating}</div>
            </div>
          ))
        ) : (
          <EmptyState title="Рейтинг загружается" detail="Таблица игроков появится после входа или ответа сервера." />
        )}
      </div>
    </MenuPanel>
  );
}

function MyMapsPanel({ myMaps, myMapsStatus, onOpenEditor }: { myMaps: MrMapSummary[]; myMapsStatus: string; onOpenEditor: () => void }) {
  return (
    <MenuPanel eyebrow="ваши карты" title="Свои карты" action={<SmallButton onClick={onOpenEditor}>Редактор</SmallButton>}>
      <div className="grid gap-3">
        {myMaps.length > 0 ? (
          myMaps.slice(0, 4).map((map) => (
            <div key={map.id} className="grid gap-3 rounded-[18px] border border-site-border bg-site-surface/[0.04] p-3 sm:grid-cols-3">
              <div className="sm:col-span-1">
                <MysteryRoomMapCard title={map.title} preview={map.preview} selected={false} badge={map.status} />
              </div>
              <div className="min-w-0 self-center sm:col-span-2">
                <div className="truncate text-sm font-black text-site-text">{map.title}</div>
                <div className="mt-1 line-clamp-2 text-xs leading-5 text-site-muted">{map.description || `Обновлена ${formatDate(map.updatedAt)}`}</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <MiniMetric value={map.matchesTotal ?? 0} label="игр" />
                  <MiniMetric value={map.activeMatches ?? 0} label="сейчас" />
                  <MiniMetric value={map.uniquePlayersTotal ?? 0} label="игроков" />
                </div>
              </div>
            </div>
          ))
        ) : (
          <EmptyState title="Карт пока нет" detail={myMapsStatus} />
        )}
      </div>
    </MenuPanel>
  );
}

function FriendsPanel({
  friends,
  friendsStatus,
  compact,
  inviteStatusByFriendId,
  onInviteFriend,
  dock = false,
}: {
  friends: MrFriend[];
  friendsStatus: string;
  compact: boolean;
  inviteStatusByFriendId: Record<number, string>;
  onInviteFriend: (friend: MrFriend) => void;
  dock?: boolean;
}) {
  const sortedFriends = sortMysteryRoomFriends(friends);
  const playingCount = sortedFriends.filter(isMysteryRoomFriendPlaying).length;
  const visibleFriends = sortedFriends.slice(0, compact ? 4 : dock ? 5 : 8);
  return (
    <MenuPanel eyebrow="друзья" title="Друзья в Mystery Room" action={<MenuPill tone="cyan">{playingCount > 0 ? `${playingCount} играют` : `${friends.length} друзей`}</MenuPill>}>
      <div className={`grid max-h-[330px] gap-2 overflow-y-auto pr-1 ${dock ? '' : 'md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2'}`}>
        {visibleFriends.length > 0 ? (
          visibleFriends.map((friend) => (
            <FriendRow
              key={friend.id}
              friend={friend}
              inviteStatus={inviteStatusByFriendId[friend.id]}
              onInviteFriend={onInviteFriend}
            />
          ))
        ) : (
          <EmptyState title="Список пуст" detail={friendsStatus} />
        )}
      </div>
    </MenuPanel>
  );
}

function SettingsPanel({
  settings,
  onChange,
  onCreateGame,
}: {
  settings: Record<MenuSettingKey, boolean>;
  onChange: (key: MenuSettingKey, value: boolean) => void;
  onCreateGame: () => void;
}) {
  return (
    <MenuPanel eyebrow="настройки" title="Настройки">
      <div className="grid gap-2">
        <SettingSwitch label="Живые эффекты меню" checked={settings.motion} onChange={(value) => onChange('motion', value)} />
        <SettingSwitch label="Компактные друзья" checked={settings.compactFriends} onChange={(value) => onChange('compactFriends', value)} />
        <SettingSwitch label="Звук меню" checked={settings.menuAudio} onChange={(value) => onChange('menuAudio', value)} />
        <SettingSwitch label="Tab сразу открывает инвентарь" checked={settings.autoInventory} onChange={(value) => onChange('autoInventory', value)} />
      </div>
      <button
        type="button"
        onClick={onCreateGame}
        className="mt-3 w-full rounded-[16px] border border-site-border bg-site-surface/[0.04] px-4 py-3 text-sm font-bold text-site-text transition hover:border-site-accent/35 hover:bg-site-surface/[0.07]"
      >
        Открыть расширенное создание
      </button>
    </MenuPanel>
  );
}

function FriendRow({
  friend,
  inviteStatus,
  onInviteFriend,
}: {
  friend: MrFriend;
  inviteStatus?: string;
  onInviteFriend: (friend: MrFriend) => void;
}) {
  const name = friend.display_name || friend.username || `user #${friend.id}`;
  const initials = name.trim().slice(0, 2).toUpperCase();
  const sent = inviteStatus === 'Отправлено';
  const loading = inviteStatus === 'Отправляю';
  const playing = isMysteryRoomFriendPlaying(friend);
  const presenceText = formatMysteryRoomFriendPresence(friend);
  return (
    <div className={`flex min-w-0 flex-wrap items-center gap-2 rounded-[16px] border px-3 py-2 sm:gap-3 ${playing ? 'border-site-warning/26 bg-site-warning/[0.07]' : 'border-site-border bg-site-surface/[0.035]'}`}>
      <div className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full border border-site-accent/20 bg-site-accent/14 text-xs font-black text-site-accent">
        {friend.avatar_url ? <img src={friend.avatar_url} alt="" className="h-full w-full object-cover" /> : initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-site-text">{name}</div>
        <div className={playing ? 'truncate text-xs font-semibold text-site-warning' : friend.is_online ? 'truncate text-xs text-site-success' : 'truncate text-xs text-site-muted'}>
          {presenceText}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onInviteFriend(friend)}
        disabled={loading}
        className="rounded-full border border-site-warning/30 bg-site-warning/14 px-3 py-1.5 text-xs font-bold text-site-warning transition hover:border-site-warning/60 hover:bg-site-warning/22 disabled:cursor-wait disabled:opacity-70"
      >
        {sent ? 'Отправлено' : loading ? '...' : 'Пригласить'}
      </button>
      {friend.game_presence?.url ? (
        <a
          href={friend.game_presence.url}
          className="shrink-0 rounded-full border border-site-success/35 bg-site-success/14 px-3 py-1.5 text-xs font-bold text-site-success transition hover:bg-site-success/22"
        >
          В лобби
        </a>
      ) : null}
    </div>
  );
}

function MenuPanel({ eyebrow, title, action, children }: { eyebrow: string; title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-[24px] border border-site-border bg-site-bg/74 p-4 shadow-[0_22px_70px_rgba(2,6,23,0.35)] backdrop-blur-xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[0.62rem] uppercase tracking-[0.24em] text-site-accent/55">{eyebrow}</div>
          <h2 className="mt-1 truncate text-xl font-black text-site-text">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function InfoPanel({ title, eyebrow, children }: { title: string; eyebrow: string; children: ReactNode }) {
  return (
    <section className="rounded-[18px] border border-site-border bg-site-surface/[0.04] p-4">
      <div className="text-[0.62rem] uppercase tracking-[0.2em] text-site-muted">{eyebrow}</div>
      <div className="mt-2 text-lg font-black text-site-text">{title}</div>
      <div className="mt-3 text-sm leading-6 text-site-secondary">{children}</div>
    </section>
  );
}

function ActionButton({ title, detail, tone, onClick }: { title: string; detail: string; tone: 'cyan' | 'amber' | 'slate'; onClick: () => void }) {
  const className =
    tone === 'cyan'
      ? 'border-site-accent/45 bg-site-accent text-site-on-accent hover:bg-site-accent'
      : tone === 'amber'
        ? 'border-site-warning/45 bg-site-warning text-site-on-accent hover:bg-site-warning'
        : 'border-site-border bg-site-surface/[0.055] text-site-text hover:border-site-border hover:bg-site-surface/[0.08]';
  return (
    <button type="button" onClick={onClick} className={`min-h-[86px] rounded-[18px] border px-4 py-4 text-left transition active:scale-[0.99] ${className}`}>
      <span className="block text-lg font-black">{title}</span>
      <span className={`mt-1 block text-xs leading-5 ${tone === 'slate' ? 'text-site-secondary' : 'text-site-muted/75'}`}>{detail}</span>
    </button>
  );
}

function SmallButton({ children, onClick }: { children: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-site-border bg-site-surface/[0.045] px-4 py-2 text-sm font-semibold text-site-text transition hover:border-site-border hover:bg-site-surface/[0.08]"
    >
      {children}
    </button>
  );
}

function MenuPill({ children, tone = 'slate' }: { children: ReactNode; tone?: 'slate' | 'cyan' | 'amber' }) {
  const className =
    tone === 'cyan'
      ? 'border-site-accent/28 bg-site-accent/12 text-site-accent'
      : tone === 'amber'
        ? 'border-site-warning/28 bg-site-warning/12 text-site-warning'
        : 'border-site-border bg-site-surface/[0.045] text-site-secondary';
  return <span className={`rounded-full border px-3 py-1 text-xs font-bold ${className}`}>{children}</span>;
}

function StatTile({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'cyan' | 'amber' }) {
  return (
    <div className={`rounded-[16px] border px-3 py-3 ${tone === 'cyan' ? 'border-site-accent/24 bg-site-accent/10' : tone === 'amber' ? 'border-site-warning/24 bg-site-warning/10' : 'border-site-border bg-site-surface/[0.035]'}`}>
      <div className="truncate text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">{label}</div>
      <div className="mt-2 truncate text-lg font-black text-site-text">{value}</div>
    </div>
  );
}

function StatusLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[14px] border border-site-border bg-site-surface/[0.035] px-3 py-2">
      <div className="text-[0.58rem] uppercase tracking-[0.14em] text-site-muted">{label}</div>
      <div className="mt-1 truncate font-bold text-site-text">{value}</div>
    </div>
  );
}

function MiniMetric({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-[12px] border border-site-border bg-site-bg/48 px-2 py-2 text-center">
      <div className="text-sm font-black text-site-text">{value}</div>
      <div className="mt-0.5 truncate text-[0.58rem] uppercase tracking-[0.12em] text-site-muted">{label}</div>
    </div>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-[18px] border border-dashed border-site-border bg-site-surface/[0.025] px-4 py-4">
      <div className="text-sm font-bold text-site-text">{title}</div>
      <div className="mt-1 text-xs leading-5 text-site-muted">{detail}</div>
    </div>
  );
}

function SettingSwitch({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between gap-3 rounded-[16px] border border-site-border bg-site-surface/[0.035] px-3 py-3 text-left transition hover:border-site-accent/26"
    >
      <span className="text-sm font-semibold text-site-text">{label}</span>
      <span className={`relative h-6 w-11 rounded-full border transition ${checked ? 'border-site-accent/45 bg-site-accent/35' : 'border-site-border bg-site-bg'}`}>
        <span className={`absolute top-1 size-4 rounded-full bg-site-surface transition ${checked ? 'left-6' : 'left-1'}`} />
      </span>
    </button>
  );
}

function AvatarSilhouette({ seed }: { seed: string }) {
  const tone = seed === 'medic' ? 'bg-site-success/20' : seed === 'engineer' ? 'bg-site-warning/20' : 'bg-site-accent-secondary/18';
  return (
    <div className={`grid h-full place-items-center ${tone}`}>
      <div className="relative h-16 w-12">
        <div className="absolute left-1/2 top-1 size-8 -translate-x-1/2 rounded-full border border-site-border bg-site-bg" />
        <div className="absolute bottom-0 left-0 h-10 w-12 rounded-t-[18px] border border-site-border bg-site-surface" />
        <div className="absolute left-1/2 top-4 h-1.5 w-7 -translate-x-1/2 rounded-full bg-site-accent/70" />
      </div>
    </div>
  );
}

function usePersistentMenuSettings(): [Record<MenuSettingKey, boolean>, (key: MenuSettingKey, value: boolean) => void] {
  const [settings, setSettings] = useState<Record<MenuSettingKey, boolean>>({
    motion: true,
    compactFriends: false,
    menuAudio: false,
    autoInventory: true,
  });

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('mysteryRoomMenuSettings');
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<Record<MenuSettingKey, boolean>>;
      setSettings((current) => ({ ...current, ...parsed }));
    } catch {
      // Некорректные локальные настройки безопасно игнорируются.
    }
  }, []);

  const update = (key: MenuSettingKey, value: boolean) => {
    setSettings((current) => {
      const next = { ...current, [key]: value };
      try {
        window.localStorage.setItem('mysteryRoomMenuSettings', JSON.stringify(next));
      } catch {
        // Настройки не критичны для запуска игры.
      }
      return next;
    });
  };

  return [settings, update];
}

function storeToneClass(tone: string): string {
  if (tone === 'cyan') return 'bg-site-accent/20 shadow-[0_0_18px_rgba(34,211,238,0.24)]';
  if (tone === 'amber') return 'bg-site-warning/20 shadow-[0_0_18px_rgba(251,191,36,0.22)]';
  return 'bg-site-success/20 shadow-[0_0_18px_rgba(52,211,153,0.22)]';
}

function useStoredMenuUser(): StoredMenuUser | null {
  const [user, setUser] = useState<StoredMenuUser | null>(null);

  useEffect(() => {
    const load = () => {
      try {
        const raw = getStoredUserRaw();
        setUser(raw ? (JSON.parse(raw) as StoredMenuUser) : null);
      } catch {
        setUser(null);
      }
    };
    load();
    window.addEventListener('lisad_auth_changed', load);
    return () => window.removeEventListener('lisad_auth_changed', load);
  }, []);

  return user;
}

function resolveMenuUserName(user: StoredMenuUser | null, profile: MrProfileResponse['profile'] | null, profileStatus: string): string {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (user?.display_name) return user.display_name;
  if (user?.username) return user.username;
  if (profile) return `Игрок #${profile.userId}`;
  return profileStatus;
}

function resolveMenuAvatar(user: StoredMenuUser | null): string | null {
  return user?.avatar_display_url || user?.avatar_url || user?.avatar || null;
}

function buildDailyTasks(profile: MrProfileResponse['profile'] | null) {
  const total = Math.max(1, profile?.matchesPlayed ?? 1);
  return [
    { label: 'Ликвидировать противников', current: Math.min(total, profile?.crimesCompleted ?? 0), target: total },
    { label: 'Выполнить скрытные устранения', current: Math.min(total, profile?.crimesExposed ?? 0), target: total },
    { label: 'Взломать терминалы', current: Math.min(total, profile?.defenderWins ?? 0), target: total },
  ];
}

function profileProgress(profile: MrProfileResponse['profile'] | null): number {
  if (!profile) return 0;
  const base = Math.max(1, profile.matchesPlayed);
  return Math.max(6, Math.min(100, Math.round(((profile.defenderWins + profile.crimesExposed) / base) * 100)));
}

function buildReferenceFriends(friends: MrFriend[]): ReferenceFriendDisplay[] {
  const sortedFriends = sortMysteryRoomFriends(friends);

  return sortedFriends.slice(0, 5).map((friend) => {
    const name = friend.display_name || friend.username || `user #${friend.id}`;
    const playing = isMysteryRoomFriendPlaying(friend);
    const url = friend.game_presence?.url?.trim();
    return {
      name,
      status: formatMysteryRoomFriendPresence(friend),
      tone: playing ? 'mission' : friend.is_online ? 'online' : 'away',
      avatarUrl: friend.avatar_url || null,
      joinUrl: url && url.length > 1 ? url : null,
    };
  });
}

function formatCompactNumber(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('ru-RU');
}

function formatMoney(value: number): string {
  return `₽ ${Math.max(0, Math.round(value)).toLocaleString('ru-RU')}`;
}

function formatMapStats(map: MrMapSummary): string {
  const games = map.matchesTotal ?? 0;
  const active = map.activeMatches ?? 0;
  if (active > 0) return `${active} сейчас · ${games} игр`;
  return `${games} игр`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'нет даты';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU');
}
